import type { InputFieldSize } from "../types/inputfield";
import {
	formControlHeight,
	formFieldBg,
	formFieldErrorColor,
	formPrimary,
	textFieldControlSx,
} from "@config/theme/formControls";

const presetSizes = new Set(["small", "medium", "large"]);

const resolveControlSize = (size: InputFieldSize) => {
	if (typeof size === "object" && size !== null) {
		return size;
	}
	return presetSizes.has(size) ? formControlHeight : formControlHeight;
};

export const TextFieldStyles = (
	isClickable: boolean | undefined,
	size: InputFieldSize = "medium",
	error?: boolean,
	multiline?: boolean,
): Record<string, unknown> => {
	const controlSize = resolveControlSize(size);
	const base = textFieldControlSx(error, controlSize) as Record<string, unknown>;
	const baseInput = (base["& input, & textarea"] as Record<string, unknown>) ?? {};
	const baseRoot = (base["& .MuiOutlinedInput-root"] as Record<string, unknown>) ?? {};

	const sharedInputStyles = {
		...baseInput,
		cursor: isClickable ? "pointer" : "text",
		"&.Mui-disabled": {
			WebkitTextFillColor: "#6B7280",
			color: "#6B7280",
			opacity: 1,
			cursor: "not-allowed",
		},
	};

	const styles: Record<string, unknown> = {
		...base,
		cursor: isClickable ? "pointer" : "text",

		"& .MuiOutlinedInput-root.Mui-disabled": {
			backgroundColor: `${formFieldBg} !important`,
			opacity: 1,
			cursor: "not-allowed",
		},

		...(multiline
			? {
					"& .MuiOutlinedInput-root": {
						...baseRoot,
						height: "auto",
						minHeight: "unset",
						alignItems: "flex-start",
					},
					"& input": sharedInputStyles,
					"& textarea": {
						...sharedInputStyles,
						height: "auto !important",
						minHeight: "auto",
						padding: "13px 15px",
						lineHeight: 1.5,
						resize: "vertical",
						boxSizing: "border-box",
					},
				}
			: {
					"& input, & textarea": sharedInputStyles,
				}),

		"& input[type='date']": {
			color: "#000",
			fontSize: "14px",
			fontWeight: "400",
			height: "24px !important",
			paddingRight: "40px",
			"&.Mui-disabled": {
				WebkitTextFillColor: "#6B7280",
				color: "#6B7280",
				opacity: 1,
				cursor: "not-allowed",
			},
			"&:invalid": {
				color: "#6B7280",
			},
			"&::-webkit-datetime-edit": {
				color: "#9ca3af",
			},
			"&:valid::-webkit-datetime-edit": {
				color: "#111827",
			},
			"&::-webkit-calendar-picker-indicator": {
				opacity: 0.6,
				cursor: "pointer",
				position: "absolute",
				right: "12px",
			},
		},

		"& .MuiInputBase-root": {
			position: "relative",
		},

		"&.Mui-focused .MuiInputAdornment-root svg": {
			color: formPrimary,
		},
		"&.Mui-error .MuiInputAdornment-root svg": {
			color: formFieldErrorColor,
		},
		"& .MuiInputAdornment-positionStart": {
			marginRight: "0px",
		},
		"& .MuiInputAdornment-positionEnd": {
			marginLeft: "0px",
		},

		"& .MuiFormHelperText-root": {
			marginTop: "4px",
			fontSize: "11px",
			marginLeft: "0px",
			fontFamily: "Poppins, sans-serif",
			color: error ? formFieldErrorColor : "#6B7280",
			lineHeight: 1,
		},
		"& .MuiFormHelperText-root.Mui-error": {
			color: formFieldErrorColor,
		},
	};

	return styles;
};
